import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
} from 'react';
import { useMediaPlayer, useMediaState } from '@vidstack/react';

declare global {
    interface Window {
        readonly webkitAudioContext?: typeof AudioContext;
    }
}

export interface ChannelState {
    readonly index: number;
    readonly volume: number;
    readonly isMuted: boolean;
    readonly isSoloed: boolean;
}

interface AudioMixerContextType {
    readonly channels: ChannelState[];
    readonly setVolume: (index: number, volume: number) => void;
    readonly toggleMute: (index: number) => void;
    readonly toggleSolo: (index: number) => void;
}

const audioNodeCache = new WeakMap<
    HTMLVideoElement,
    MediaElementAudioSourceNode
>();

const extractVideoElement = (provider: unknown): HTMLVideoElement | null => {
    if (
        typeof provider === 'object' &&
        provider !== null &&
        'video' in provider
    ) {
        const video = (provider as Record<string, unknown>).video;
        if (video instanceof HTMLVideoElement) return video;
    }
    return null;
};

const getAudioSourceNode = (
    ctx: AudioContext,
    video: HTMLVideoElement,
): MediaElementAudioSourceNode => {
    const cachedNode = audioNodeCache.get(video);
    if (cachedNode) {
        cachedNode.disconnect();
        return cachedNode;
    }
    const newNode = ctx.createMediaElementSource(video);
    audioNodeCache.set(video, newNode);
    return newNode;
};

const generateInitialChannels = (count: number): ChannelState[] =>
    Array.from({ length: count }, (_, index) => ({
        index,
        volume: 1.0,
        isMuted: false,
        isSoloed: false,
    }));

const AudioMixerContext = createContext<AudioMixerContextType | null>(null);

export const AudioMixerProvider: React.FC<{
    readonly children: React.ReactNode;
}> = ({ children }) => {
    const player = useMediaPlayer();

    const canPlay = useMediaState('canPlay');
    const isPlaying = useMediaState('playing');

    const [channels, setChannels] = useState<ChannelState[]>([]);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const gainsRef = useRef<readonly GainNode[]>([]);

    useEffect(() => {
        const ctx = audioCtxRef.current;
        if (isPlaying && ctx?.state === 'suspended') {
            void ctx.resume();
        }
    }, [isPlaying]);

    useEffect(() => {
        if (!canPlay || !player) return;

        const videoNode = extractVideoElement(player.provider);

        if (!videoNode || videoNode.crossOrigin !== 'anonymous') {
            setChannels([]);
            return;
        }

        const AudioContextClass =
            window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;

        const audioCtx = new AudioContextClass();
        audioCtxRef.current = audioCtx;

        const sourceNode = getAudioSourceNode(audioCtx, videoNode);
        const sourceChannelCount =
            sourceNode.channelCount > 0 ? sourceNode.channelCount : 2;

        const splitterNode = audioCtx.createChannelSplitter(sourceChannelCount);
        const mergerNode = audioCtx.createChannelMerger(2);

        sourceNode.connect(splitterNode);

        const calculatedGains = Array.from(
            { length: sourceChannelCount },
            (_, i) => {
                const gainNode = audioCtx.createGain();
                splitterNode.connect(gainNode, i);

                const destination = i % 2 === 0 ? 0 : 1;
                gainNode.connect(mergerNode, 0, destination);

                return gainNode;
            },
        );

        mergerNode.connect(audioCtx.destination);

        gainsRef.current = Object.freeze(calculatedGains);
        setChannels(generateInitialChannels(sourceChannelCount));

        return () => {
            gainsRef.current = [];
            void audioCtx.close();
        };
    }, [player, canPlay]);

    const dispatchAudioMatrixUpdate = useCallback(
        (nextChannels: readonly ChannelState[]): void => {
            const audioCtx = audioCtxRef.current;
            const hardwareGains = gainsRef.current;

            if (!audioCtx || hardwareGains.length === 0) return;
            if (audioCtx.state === 'suspended') void audioCtx.resume();

            const isAnySoloed = nextChannels.some((c) => c.isSoloed);

            nextChannels.forEach((ch) => {
                const node = hardwareGains[ch.index];
                if (!node) return;

                const shouldSilence =
                    ch.isMuted || (isAnySoloed && !ch.isSoloed);
                node.gain.setValueAtTime(
                    shouldSilence ? 0 : ch.volume,
                    audioCtx.currentTime,
                );
            });

            setChannels([...nextChannels]);
        },
        [],
    );

    const setVolume = useCallback(
        (index: number, volume: number) => {
            dispatchAudioMatrixUpdate(
                channels.map((c) => (c.index === index ? { ...c, volume } : c)),
            );
        },
        [channels, dispatchAudioMatrixUpdate],
    );

    const toggleMute = useCallback(
        (index: number) => {
            dispatchAudioMatrixUpdate(
                channels.map((c) =>
                    c.index === index ? { ...c, isMuted: !c.isMuted } : c,
                ),
            );
        },
        [channels, dispatchAudioMatrixUpdate],
    );

    const toggleSolo = useCallback(
        (index: number) => {
            dispatchAudioMatrixUpdate(
                channels.map((c) => ({
                    ...c,
                    isSoloed: c.index === index ? !c.isSoloed : false,
                })),
            );
        },
        [channels, dispatchAudioMatrixUpdate],
    );

    return (
        <AudioMixerContext.Provider
            value={{ channels, setVolume, toggleMute, toggleSolo }}
        >
            {children}
        </AudioMixerContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAudioMixer = (): AudioMixerContextType => {
    const context = useContext(AudioMixerContext);
    if (!context)
        throw new Error(
            'useAudioMixer must be used within an AudioMixerProvider',
        );
    return context;
};
